import { zodResolver } from "@hookform/resolvers/zod";
import { deleteField } from "firebase/firestore";
import React from "react";
import { Button, Card, Col, Form, Row, Spinner } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { FaSave } from "react-icons/fa";
import { useParams } from "react-router";
import { z } from "zod";

import * as api from "../../api/api";
import {
    OrganizationDocument,
    useOrganizationDocument,
} from "../../api/firestoreWrappers.generated.jsx";
import { Breadcrumbs, breadcrumbPaths } from "../../components/Breadcrumbs";
import CancelButton from "../../components/CancelButton/index.jsx";
import TextFormGroup from "../../components/FormGroups/TextFormGroup.jsx";
import { ConfigUploadJSONButton } from "../../components/JsonButtons/index.jsx";
import Layout, { Title } from "../../components/Layout/Layout.jsx";
import { useUser } from "../../contexts/user_provider.jsx";
import urls from "../../urls.jsx";

function OrganizationSettings(props) {
    const [isLoading, setIsLoading] = React.useState(false);

    const optionalNumberFromString = z.preprocess(
        (value) =>
            typeof value === "string" && value.trim() === ""
                ? undefined
                : value,
        z.coerce
            .number({ invalid_type_error: "Enter a valid number" })
            .nonnegative()
            .optional(),
    );
    const OrganizationSettingsConfigSchema = z.object({
        name: z.string().min(1, { message: "Name is required" }),

        settings: z.object({
            retention: z.object({
                measurements: optionalNumberFromString,
                results: optionalNumberFromString,
                telemetry: optionalNumberFromString,
            }),
        }),
    });

    const {
        control,
        handleSubmit,
        reset,
        setValue,
        formState: { errors, isDirty, isSubmitting },
    } = useForm({
        resolver: zodResolver(OrganizationSettingsConfigSchema),
    });

    const onSubmit = async (organizationSettingsUpdate) => {
        const retention = organizationSettingsUpdate.settings.retention;
        const updates = {
            name: organizationSettingsUpdate.name,
            "settings.retention.measurements":
                retention.measurements ?? deleteField(),
            "settings.retention.results": retention.results ?? deleteField(),
            "settings.retention.telemetry":
                retention.telemetry ?? deleteField(),
        };

        await OrganizationDocument(props.organizationId).update(updates);
        reset(organizationSettingsUpdate); // Reset the form with the latest saved state
    };

    React.useEffect(() => {
        if (props.organization) {
            setIsLoading(false);
            reset(props.organization);
        } else {
            setIsLoading(true);
        }
    }, [props.organization, reset]);

    return (
        <Card.Body className="py-2">
            {isLoading ? (
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            ) : (
                <Form onSubmit={handleSubmit(onSubmit)}>
                    <TextFormGroup
                        label="Name"
                        name="name"
                        control={control}
                        errors={errors}
                        setValue={setValue}
                    />

                    <h2>Retention</h2>
                    <TextFormGroup
                        label="Measurements"
                        name="settings.retention.measurements"
                        control={control}
                        errors={errors}
                        defaultValue="28"
                        unitText="days"
                        setValue={setValue}
                    />
                    <TextFormGroup
                        label="Results"
                        name="settings.retention.results"
                        control={control}
                        errors={errors}
                        defaultValue="28"
                        unitText="days"
                        setValue={setValue}
                    />
                    <TextFormGroup
                        label="Telemetry"
                        name="settings.retention.telemetry"
                        control={control}
                        errors={errors}
                        defaultValue="28"
                        unitText="days"
                        setValue={setValue}
                    />

                    <Card.Body className="p-0">
                        <Button
                            variant="success"
                            disabled={isSubmitting || !isDirty}
                            type="submit"
                            className="me-2"
                        >
                            <FaSave className="mb-1 me-1" />
                            {isSubmitting ? "Saving..." : "Save"}
                        </Button>
                        <CancelButton
                            to={urls.organization(props.organizationId)}
                        />
                    </Card.Body>
                </Form>
            )}
        </Card.Body>
    );
}

function CloudConnectionSettings(props) {
    const { getUserIsAdmin } = useUser();
    const [isSubmittingConnection, setIsSubmittingConnection] =
        React.useState(false);

    async function handleAddConnection(credentials) {
        const bucketPath = prompt(
            "Enter bucket path (e.g. gs://bucket, s3://bucket or, az://blob_path)",
        );

        if (!bucketPath) return;

        setIsSubmittingConnection(true);

        try {
            await api.addCloudConnection(
                props.organizationId,
                bucketPath,
                credentials,
            );
        } catch {
            alert("Failed to create connection");
        } finally {
            setIsSubmittingConnection(false);
        }
    }

    async function handleDeleteConnection(id) {
        if (!confirm("Delete this connection?")) return;

        try {
            await api.deleteCloudConnection(props.organizationId, id);
        } catch {
            alert("Failed to delete connection");
        }
    }

    return (
        <>
            {getUserIsAdmin() && (
                <Card className="mt-2">
                    <Card.Body>
                        <Card.Title>Cloud Connections</Card.Title>

                        <Row>
                            <Col xs={12} md={6}>
                                <ConfigUploadJSONButton
                                    disabled={isSubmittingConnection}
                                    onUpload={handleAddConnection}
                                />
                            </Col>
                        </Row>

                        <hr />

                        {Object.entries(
                            props.organization?.cloud_connection?.connections ??
                                {},
                        ).length === 0 && (
                            <div className="text-muted">
                                No connections configured
                            </div>
                        )}

                        {Object.entries(
                            props.organization?.cloud_connection?.connections ??
                                {},
                        ).map(([id, name]) => (
                            <div
                                key={id}
                                className="d-flex justify-content-between align-items-center border rounded p-2 mb-2"
                            >
                                <div>{name}</div>

                                <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => handleDeleteConnection(id)}
                                >
                                    Delete
                                </Button>
                            </div>
                        ))}
                    </Card.Body>
                </Card>
            )}
        </>
    );
}

export default function OrganizationSettingsPage() {
    const { organizationId } = useParams();
    const [organization] = useOrganizationDocument(organizationId, {});

    return (
        <Layout>
            <title>{`${organization?.name} Settings | MyCorvus`}</title>
            <Breadcrumbs
                path={breadcrumbPaths.organizationSettings}
                organization={organization}
            />
            <Card className="p-0">
                <Title
                    title={"Organization Settings"}
                    backButtonUrl={urls.organization(organizationId)}
                ></Title>
            </Card>

            <Card className="mt-2">
                <Card.Body>
                    <OrganizationSettings
                        organizationId={organizationId}
                        organization={organization}
                    ></OrganizationSettings>
                </Card.Body>
            </Card>

            <CloudConnectionSettings
                organizationId={organizationId}
                organization={organization}
            ></CloudConnectionSettings>
        </Layout>
    );
}
